import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast as toastify } from 'react-toastify';
import { formatTime } from 'chameleon/packages/blue-book-player/utils';
import PauseScreen from './PauseScreen';
import Icon from 'chameleon/packages/blue-book-player/library/icon';
import useIsChromebook from '../hooks/useIsChromebook';

const getMilliseconds = (seconds) => seconds * 1000;
const secondsToMinutes = (seconds) => Math.floor(seconds / 60);

const BlueBookTimer = React.forwardRef((props, _ref) => {
  const [timeUsed, setTimeUsed] = useState(0);
  const [expirationDate, setExpirationDate] = useState(new Date());
  const [toastId, setToastId] = useState(null);
  const [lastExtendTimeCheck, setLastExtendTimeCheck] = useState(0);
  const isChromebook = useIsChromebook();

  const {
    timerInfo,
    assignmentPlayer,
    showTimer,
    isPaused,
    quizTitle,
    userData,
    resumeTest,
    setTimerTriggerButton,
    onCountdownEnd = () => {},
    setShowTimer,
    checkTimeExtension,
    pauseTimeUsed,
    triggerTimerVisibility,
    assignmentId,
  } = props;

  const { me } = userData || {};
  const { timeElapsed } = timerInfo || {};

  const FIVE_MINUTES = 300;
  const CHECK_TIME_EXTENSION_INTERVAL = 10000;

  const showToast = () => {
    const toastConfig = {
      autoClose: false,
      closeButton: (
        <div className="self-center ml-10">
          <Icon id="close" width="20" height="20" />
        </div>
      ),
      ...(isChromebook ? { style: { top: '4em' } } : {}),
    };
    const id = toastify(
      `${secondsToMinutes(FIVE_MINUTES)} minutes left.`,
      toastConfig,
    );
    setToastId(id);
  };

  const triggerShowTimerMutation = () => {
    triggerTimerVisibility({
      variables: {
        assignmentId,
        isTimerHidden: false,
      },
    }).then(() => {
      setShowTimer(true);
    });
  };

  const showFiveMinutesNotification = () => {
    const timeUsedInMinutes = secondsToMinutes(timeUsed);
    if (timeUsedInMinutes === secondsToMinutes(FIVE_MINUTES)) {
      showToast();
      triggerShowTimerMutation();
    }
  };

  useEffect(() => {
    return () => {
      if (toastId) {
        toastify.dismiss(toastId);
      }
    };
  }, [toastId]);

  useEffect(() => {
    if (isPaused && pauseTimeUsed > 0.0) {
      setTimeUsed(assignmentPlayer?.timedSession?.totalTime - pauseTimeUsed);
    }
  }, [isPaused, pauseTimeUsed]);

  const getExpirationDate = (timeUsed) => {
    return new Date(Date.now() + getMilliseconds(timeUsed));
  };

  useEffect(() => {
    const { timedSession } = assignmentPlayer || {};
    const { totalTime, timeElapsed: assignmentTimeElapsed } =
      timedSession || {};

    if (!totalTime) return;

    if (timeElapsed && timeElapsed > totalTime) {
      setTimeUsed(0);
      onCountdownEnd();
      return;
    }

    if (!timeUsed) {
      setTimeUsed(totalTime - (timeElapsed || assignmentTimeElapsed));
    }
    const timeUsedInMinutes = secondsToMinutes(
      totalTime - (timeElapsed || assignmentTimeElapsed),
    );
    if (timeUsedInMinutes < secondsToMinutes(FIVE_MINUTES)) {
      setTimerTriggerButton(false);
      if (!showTimer) {
        triggerShowTimerMutation();
      }
    }

    setExpirationDate(
      getExpirationDate(
        timeUsed || totalTime - (timeElapsed || assignmentTimeElapsed),
      ),
    );
  }, [isPaused, assignmentPlayer, showTimer]);

  useEffect(() => {
    const { totalTime } = timerInfo || {};

    if (!totalTime || !timeElapsed) return;

    setExpirationDate(getExpirationDate(totalTime - timeElapsed));
  }, [timerInfo]);

  useEffect(() => {
    const interval = setInterval(tickTime, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [timeUsed, expirationDate]);

  const tickTime = async () => {
    if (isPaused) return;
    if (timeUsed <= 0) {
      onCountdownEnd();
      return;
    }
    const now = new Date();

    if (now - lastExtendTimeCheck >= CHECK_TIME_EXTENSION_INTERVAL) {
      await checkTimeExtension();
      setLastExtendTimeCheck(now);
    }

    const seconds = (expirationDate.getTime() - now.getTime()) / 1000;
    setTimeUsed(seconds);

    if (seconds > FIVE_MINUTES) return;
    setTimerTriggerButton(false);
    showFiveMinutesNotification();
  };

  return (
    <>
      <div
        className={`bb-mobile:absolute bb-mobile:left-4 min-w-[70px] text-center${!showTimer ? ' hidden' : ''}`}
        role="region"
        aria-label="timer"
        tabIndex={-1}
        data-test-id="bluebook-timer-region"
        ref={_ref}
      >
        <div
          className={`w-full h-[28px] text-22 font-medium text-center ${
            timeUsed < FIVE_MINUTES ? 'text-red-600' : 'text-gray-900'
          } leading-snug`}
          data-test-id="bluebook-countdown-time"
          data-cy="bluebook-countdown-time"
        >
          {formatTime(timeUsed)}
        </div>
      </div>
      {isPaused && (
        <PauseScreen
          quizTitle={quizTitle}
          remainingTime={timeUsed}
          fullName={`${me?.firstName} ${me?.lastName}`}
          resumeTest={resumeTest}
          data-test-id="bluebook-pause-screen"
          data-cy="bluebook-pause-screen"
        />
      )}
    </>
  );
});

BlueBookTimer.propTypes = {
  showTimer: PropTypes.bool,
  isPaused: PropTypes.bool,
  quizTitle: PropTypes.string,
  userData: PropTypes.shape({
    me: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  }),
  resumeTest: PropTypes.func,
  setTimerTriggerButton: PropTypes.func,
  onCountdownEnd: PropTypes.func,
  assignmentPlayer: PropTypes.shape({
    timedSession: PropTypes.shape({
      totalTime: PropTypes.number,
    }),
  }),
  setShowTimer: PropTypes.func,
  timerInfo: PropTypes.object,
  checkTimeExtension: PropTypes.func,
};

BlueBookTimer.defaultProps = {
  showTimer: true,
  isPaused: false,
  quizTitle: '',
  userData: {},
  resumeTest: () => {},
  setTimerTriggerButton: () => {},
  onCountdownEnd: () => {},
  assignmentPlayer: {},
  setShowTimer: () => {},
  timerInfo: {},
  checkTimeExtension: () => {},
};

export default BlueBookTimer;
